"use client";

import { useEffect, useState, type CSSProperties } from "react";

import { ToolSectionHeader } from "@/components/tools/edge-bars";
import { cn } from "@/lib/utils";

export const TYPICAL_ACTIVITY_BY_HOUR = [
  3, 2, 2, 2, 3, 4,
  6, 8, 12, 18, 26, 34,
  30, 28, 32, 46, 68, 78,
  88, 96, 100, 76, 42, 18,
] as const;

const HOUR_LABELS: Record<number, string> = {
  0: "12a",
  6: "6a",
  12: "12p",
  18: "6p",
};

function getCurrentEasternHour(): number {
  const hour = Number(
    new Intl.DateTimeFormat("en-US", {
      hour: "numeric",
      hour12: false,
      timeZone: "America/New_York",
    }).format(new Date()),
  );

  if (!Number.isFinite(hour)) return 0;
  return hour === 24 ? 0 : hour;
}

export function PeakTimeChart({
  className,
  currentHourET,
}: {
  className?: string;
  currentHourET?: number;
}) {
  const [currentHour, setCurrentHour] = useState<number | null>(null);
  const maxActivity = Math.max(...TYPICAL_ACTIVITY_BY_HOUR);

  useEffect(() => {
    setCurrentHour(currentHourET ?? getCurrentEasternHour());
  }, [currentHourET]);

  return (
    <section
      aria-label="Typical hourly board activity in Eastern Time"
      className={cn(
        "w-full rounded-panel border border-white/[0.06] bg-surface-raised/80 p-3 text-left shadow-sm shadow-black/20",
        className,
      )}
    >
      <ToolSectionHeader
        label="When the board fills up (ET)"
        rightSlot={(
          <span className="text-[10px] font-medium uppercase tracking-[0.14em] text-muted-foreground/55">
            typical week
          </span>
        )}
        className="mb-3 flex items-center justify-between gap-3"
      />

      <div className="grid h-24 grid-cols-[repeat(24,minmax(0,1fr))] items-end gap-1 rounded-md border border-white/[0.06] bg-surface-base/70 px-2 pb-2 pt-4">
        {TYPICAL_ACTIVITY_BY_HOUR.map((activity, hour) => {
          const heightPercent = Math.max((activity / maxActivity) * 100, 6);
          const isCurrentHour = currentHour !== null && hour === currentHour;

          return (
            <div key={hour} className="relative flex h-full items-end justify-center">
              {isCurrentHour && (
                <span
                  data-testid="peak-time-current-hour"
                  aria-label={`Current hour marker: ${hour}:00 ET`}
                  className="absolute -top-2 h-[calc(100%+0.75rem)] w-px rounded-full bg-primary/45 shadow-[0_0_8px_rgba(var(--primary-rgb),0.35)]"
                />
              )}
              <span
                data-testid="peak-time-bar"
                aria-label={`${hour}:00 ET typical activity`}
                className={cn(
                  "relative z-10 h-[var(--value)] w-full max-w-[0.55rem] rounded-t-sm bg-accent-value/85 shadow-[0_0_10px_rgba(109,255,4,0.12)]",
                  isCurrentHour ? "ring-1 ring-primary/60" : "",
                )}
                style={{ "--value": `${heightPercent}%` } as CSSProperties}
              />
            </div>
          );
        })}
      </div>

      <div className="mt-1 grid grid-cols-[repeat(24,minmax(0,1fr))] gap-1 px-2" aria-hidden="true">
        {TYPICAL_ACTIVITY_BY_HOUR.map((_, hour) => (
          <span key={hour} className="text-center text-[9px] font-medium text-muted-foreground/45">
            {HOUR_LABELS[hour] ?? ""}
          </span>
        ))}
      </div>

      <p className="mt-2 text-[11px] leading-snug text-muted-foreground/65">
        Typical week pattern — quiet overnight, a smaller late-morning bump, and the steepest ramp from 4–10p ET.
      </p>
    </section>
  );
}
